import { z } from 'zod';
/**
 * Payload shape for `GET /api/dashboard/session-trends`.
 *
 * Two headline averages (last 7d) + 30 daily buckets for trend
 * sparklines. Each bucket is THIS DAY'S sessions averaged, not
 * a rolling-30d smoothed momentum — the line reads as actual
 * day-over-day change, which is what an operator wants to see
 * when a kWh number ticks up week-over-week.
 *
 * Buckets with zero completed sessions return `null` for both
 * averages. The frontend skips null points in the sparkline so
 * the line draws across gaps (a long no-activity stretch shows
 * up as a visible horizontal span).
 *
 * Fields scoped to completed sessions only (status in
 * COMPLETED_STATUSES). Active and failed sessions don't have
 * a clean duration to average.
 */
export const SessionTrendBucketSchema = z.object({
    /** ISO date `YYYY-MM-DD` for this bucket. UTC. */
    date: z.string(),
    /** Average session duration in minutes for this day's
     *  completed sessions. Null when count = 0. */
    avg_duration_minutes: z.number().nonnegative().nullable(),
    /** Average kWh delivered per completed session this day.
     *  Null when count = 0. */
    avg_kwh: z.number().nonnegative().nullable(),
    /** Completed session count for the day. Drives null-bucket
     *  rendering on the frontend (skip points where count = 0). */
    session_count: z.number().int().nonnegative(),
});
export const SessionTrendsResponseSchema = z.object({
    /** Headline avg duration over the last 7 days (single number,
     *  not a per-day average of averages). Drives the stat block
     *  at the top of the widget. Null when no completed sessions
     *  in the window. */
    avg_duration_minutes_7d: z.number().nonnegative().nullable(),
    /** Headline avg kWh over the last 7 days. Same null
     *  semantics. */
    avg_kwh_7d: z.number().nonnegative().nullable(),
    /** 30 daily buckets, oldest first. Index 0 is 29 days ago,
     *  index 29 is today. Always exactly 30 entries — days with
     *  no sessions are still emitted with null averages so the
     *  sparkline x-axis stays evenly spaced. */
    daily_buckets: z.array(SessionTrendBucketSchema).length(30),
    is_scoped: z.boolean(),
    generated_at: z.string(),
});
//# sourceMappingURL=dashboard-session-trends.js.map